import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import React, { useContext, useMemo, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { Company, Contact, CustomerCreateDTO, CustomerDetailDTO, Location, Note } from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { Grid } from '_lib/components/Grid/Grid';
import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Typography } from '_lib/components/Typography/Typography';
import { CustomerForm } from '_lib/featureComponents/Customer/CustomerForm';
import { CustomerPhoneCallHistory } from '_lib/featureComponents/CustomerPhoneCallHistory/CustomerPhoneCallHistory';
import {
  PrimaryHeader,
  StyledDivider,
  StyledTab,
  StyledTabs,
  PrimaryHeaderIconButton,
  ContentBox
} from '_lib/styles/PhoneCall.styled';
import { CustomerContext, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { customerDisplayName } from '_lib/utils/helpers';

import { reportException } from '_lib/utils/sentryService';

import { Contacts } from '../Contacts/Contacts';
import { CustomerDetailDisplay } from '../CustomerDetailDisplay/CustomerDetailDisplay';
import { CustomerJobHistory } from '../CustomerJobHistory/CustomerJobHistory';
import { CustomerMemberships } from '../CustomerMemberships/CustomerMemberships';
import { LocationsCard } from '../LocationsCard/LocationsCard';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

interface CustomerDetailProps {
  onCustomerUpdated: (customer: CustomerDetailDTO) => void;
  onCustomerLocationsUpdated: (locations: Location[]) => void;
  onLocationContactsUpdated: (locationProviderId: string, contacts: Contact[]) => void;
  onCloseCustomer: () => void;
  customerContext: CustomerContext;
  selectedLocation?: Location;
  locationContactsMap: {
    [locationProviderId: string]: Contact[];
  };
  phoneCallURLState: PhoneCallURLState;
  company: Company;
  hideAllTabs?: boolean; // This is required because nested tabs are not 'rendered', causing an error
}

export const CustomerDetail = (props: CustomerDetailProps): React.ReactElement => {
  const {
    onCustomerUpdated,
    onCustomerLocationsUpdated,
    onLocationContactsUpdated,
    onCloseCustomer,
    customerContext,
    selectedLocation,
    locationContactsMap,
    phoneCallURLState,
    company,
    hideAllTabs
  } = props;

  const [tabIndex, setTabIndex] = useState(0);
  const [customerBeingEdited, setCustomerBeingEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [validationErrors, setValidationErrors] = useState<string[] | undefined>(undefined);

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(company.id), [api, company.id]);
  const contactsResource = useMemo(
    () => serviceTitanApi.customers.contacts(customerContext.customer.providerId),
    [customerContext.customer.providerId, serviceTitanApi.customers]
  );

  const handleCustomerFormUpdate: ApiSubmitHandler<CustomerCreateDTO> = async (formCustomer, { setFormErrors }) => {
    try {
      setIsSaving(true);
      setValidationErrors(undefined);
      // TODO - pick out customer patch fields until we can refactor CustomerForm to be a generic with support for
      //        our create and update use-cases
      const { locations, contacts, notes, useAddressForLocation, ...customerPatchDto } = formCustomer;
      const updatedCustomer = await serviceTitanApi.customers.patch(
        customerContext.customer.providerId,
        customerPatchDto
      );

      onCustomerUpdated({
        ...updatedCustomer,
        contacts: customerContext.customer.contacts,
        notes: customerContext.customer.notes
      });
      setCustomerBeingEdited(false);
    } catch (err) {
      if (err instanceof BadRequestError) {
        setValidationErrors(err.responseData?.nonFieldErrors);
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomerContactsUpdated = (contacts: Contact[]) => {
    onCustomerUpdated({ ...customerContext.customer, contacts });
  };

  const handleCustomerNotesUpdated = (notes: Note[]) => {
    onCustomerUpdated({ ...customerContext.customer, notes });
  };

  return (
    <>
      <PrimaryHeader>
        <Grid container sx={{ paddingX: 1.5, paddingY: 1.5 }}>
          <Grid item xs={10}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant='h3' text={customerDisplayName(customerContext.customer)} />
              <MaskedPhoneDisplay variant='h3' preText='CID: ' text={phoneCallURLState.callerId ?? ''} />
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexWrap: 'nowrap',
                flexDirection: 'row',
                width: '100%'
              }}>
              <>
                {customerBeingEdited ? (
                  <PrimaryHeaderIconButton
                    disabled={isSaving}
                    key={'customer-selected-customer-form-submit'}
                    type='submit'
                    form='st-api-call-pop-customer-form'
                    size='small'>
                    <SaveIcon />
                  </PrimaryHeaderIconButton>
                ) : (
                  <PrimaryHeaderIconButton
                    onClick={() => {
                      setTabIndex(0);
                      setCustomerBeingEdited(true);
                    }}
                    disabled={isSaving}
                    size='small'>
                    <EditIcon />
                  </PrimaryHeaderIconButton>
                )}
                <PrimaryHeaderIconButton
                  onClick={() => {
                    if (customerBeingEdited) setCustomerBeingEdited(false);
                    else onCloseCustomer();
                  }}
                  disabled={isSaving}>
                  <CloseIcon />
                </PrimaryHeaderIconButton>
              </>
            </Box>
          </Grid>
        </Grid>
        <StyledDivider />
        {!hideAllTabs && (
          <StyledTabs
            value={tabIndex}
            onChange={(_event: React.SyntheticEvent, newValue: number) => setTabIndex(newValue)}>
            {customerBeingEdited ? (
              <StyledTab label={'EDIT CUSTOMER'} id='simple-tab-0' />
            ) : (
              [
                <StyledTab label={'SUMMARY'} id='simple-tab-0' key={0} />,
                <StyledTab label={'CALL HISTORY'} id='simple-tab-1' key={1} />,
                <StyledTab label={'JOB HISTORY'} id='simple-tab-2' key={2} />,
                <StyledTab label={'MEMBERSHIPS'} id='simple-tab-3' key={3} />
              ]
            )}
          </StyledTabs>
        )}
      </PrimaryHeader>

      <TabPanel currentIndex={tabIndex} index={0}>
        <ContentBox>
          <BackdropSpinner open={isSaving} />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          {customerBeingEdited ? (
            <CustomerForm
              customer={customerContext.customer}
              onCustomerSubmit={handleCustomerFormUpdate}
              contactsChild={
                <Contacts
                  contactsResource={contactsResource}
                  onContactsUpdated={handleCustomerContactsUpdated}
                  contacts={customerContext.customer.contacts}
                />
              }
              locationsChild={
                <LocationsCard
                  companyId={company.id}
                  providerCustomerId={customerContext.customer.providerId}
                  locations={customerContext.locations}
                  locationContactsMap={locationContactsMap}
                  onLocationsUpdated={onCustomerLocationsUpdated}
                  onLocationContactsUpdated={onLocationContactsUpdated}
                />
              }
            />
          ) : (
            <CustomerDetailDisplay
              companyId={company.id}
              customerContext={customerContext}
              locationContactsMap={locationContactsMap}
              onContactsUpdated={handleCustomerContactsUpdated}
              onLocationsUpdated={onCustomerLocationsUpdated}
              onLocationContactsUpdated={onLocationContactsUpdated}
              onNotesUpdated={handleCustomerNotesUpdated}
              timezone={company.timezone}
            />
          )}
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={1}>
        <ContentBox>
          {tabIndex === 1 && (
            <CustomerPhoneCallHistory company={company} providerCustomerId={customerContext.customer.providerId} />
          )}
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={2}>
        <ContentBox>
          {tabIndex === 2 && (
            <CustomerJobHistory
              locations={customerContext.locations}
              companyId={company.id}
              customerId={customerContext.customer.providerId}
              selectedLocation={selectedLocation}
              companyTimezone={company.timezone}
            />
          )}
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={3}>
        <ContentBox>
          {tabIndex === 3 && (
            <CustomerMemberships
              locations={customerContext.locations}
              companyId={company.id}
              providerCustomerId={customerContext.customer.providerId}
              selectedLocation={selectedLocation}
            />
          )}
        </ContentBox>
      </TabPanel>
    </>
  );
};
